import React, { useEffect, useState, useCallback } from "react";
import { Card, Table, Button, Modal, Form, Row, Col } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";

const emptyForm = { isubject_isid: "", isubject_name: "" };

export default function ManageIslamicSubject() {
  const { isa_id } = useParams();
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;

  const [subjects, setSubjects]   = useState([]);
  const [assignInfo, setAssignInfo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm]           = useState(emptyForm);

  const fetchSubjects = useCallback(() => {
    if (!isa_id) return;
    setLoading(true);
    axiosInstance.get(`/api/islamic/getSubjects/${isa_id}`)
      .then(r => setSubjects(r.data))
      .catch(() => setSubjects([]))
      .finally(() => setLoading(false));
  }, [isa_id, setLoading]);

  useEffect(() => {
    fetchSubjects();
    axiosInstance.get(`/api/islamic/getAssignSyllabusById/${isa_id}`)
      .then(r => setAssignInfo(r.data))
      .catch(() => {});
  }, [fetchSubjects, isa_id]);

  const openAdd = () => { setEditTarget(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (sub) => { setEditTarget(sub); setForm({ isubject_isid: sub.isubject_isid, isubject_name: sub.isubject_name }); setShowModal(true); };

  const handleSave = async () => {
    if (!form.isubject_isid || !form.isubject_name) {
      Swal.fire(t.manageSubject.incompleteTitle, t.manageSubject.incompleteMsg, "warning"); return;
    }
    try {
      if (editTarget) {
        await axiosInstance.post("/api/islamic/updateSubject", { ...form, isubject_id: editTarget.isubject_id });
        Swal.fire(t.manageSubject.successTitle, t.manageSubject.successUpdate, "success");
      } else {
        await axiosInstance.post("/api/islamic/addSubject", { ...form, isa_id });
        Swal.fire(t.manageSubject.successTitle, t.manageSubject.successAdd, "success");
      }
      setShowModal(false);
      fetchSubjects();
    } catch (err) {
      Swal.fire(t.manageSubject.errorTitle, err.response?.data?.message || t.common.error, "error");
    }
  };

  const handleDelete = (sub) => {
    Swal.fire({
      title: t.manageSubject.confirmDelete(sub.isubject_isid),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t.manageSubject.confirmBtn,
      cancelButtonText: t.manageSubject.cancelBtn,
    }).then(r => {
      if (!r.isConfirmed) return;
      axiosInstance.post("/api/islamic/deleteSubject", { isubject_id: sub.isubject_id })
        .then(() => { Swal.fire(t.manageSubject.successTitle, t.manageSubject.successDelete, "success"); fetchSubjects(); })
        .catch(err => Swal.fire(t.manageSubject.errorTitle, err.response?.data?.message || t.common.error, "error"));
    });
  };

  return (
    <React.Fragment>
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <ol className="breadcrumb fs-sm mb-1">
            <li className="breadcrumb-item"><Link to="/dashboard/school-statistic">{t.manageSubject.breadHome}</Link></li>
            <li className="breadcrumb-item"><Link to="/islamic/manage-curriculum">{t.manageSubject.breadSyllabus}</Link></li>
            <li className="breadcrumb-item active">{t.manageSubject.breadCurrent}</li>
          </ol>
          <h4 className="main-title mb-0">{t.manageSubject.pageTitle}</h4>
          <p className="text-secondary fs-sm mb-0">
            {assignInfo
              ? `${assignInfo.ic_class} — ${assignInfo.is_programCode} (${assignInfo.iss_year})`
              : `ID: ${isa_id}`}
          </p>
        </div>
        <Button size="sm" variant="primary" className="mt-2 mt-sm-0" onClick={openAdd}>
          <i className="ri-add-line me-1"></i>{t.manageSubject.addBtn}
        </Button>
      </div>

      <Card className="card-one">
        <Card.Header><Card.Title as="h6" className="mb-0">{t.manageSubject.listTitle(subjects.length)}</Card.Title></Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0">
            <thead className="table-secondary">
              <tr>
                <th>#</th>
                <th>{t.manageSubject.colCode}</th>
                <th>{t.manageSubject.colName}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {subjects.length === 0 && (
                <tr><td colSpan={4} className="text-center text-secondary py-4">{t.manageSubject.noSubjects}</td></tr>
              )}
              {subjects.map((s, i) => (
                <tr key={s.isubject_id}>
                  <td className="text-secondary">{i + 1}</td>
                  <td><span className="fw-medium">{s.isubject_isid}</span></td>
                  <td className="arabic-text">{s.isubject_name}</td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => openEdit(s)}><i className="ri-edit-line"></i></Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(s)}><i className="ri-delete-bin-line"></i></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editTarget ? t.manageSubject.modalEditTitle : t.manageSubject.modalAddTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={4}>
              <Form.Label className="fw-medium">{t.manageSubject.labelCode}</Form.Label>
              <Form.Control value={form.isubject_isid} onChange={e => setForm(f => ({ ...f, isubject_isid: e.target.value }))} placeholder={t.manageSubject.codePlaceholder} />
            </Col>
            <Col xs={8}>
              <Form.Label className="fw-medium">{t.manageSubject.labelName}</Form.Label>
              <Form.Control
                value={form.isubject_name}
                onChange={e => setForm(f => ({ ...f, isubject_name: e.target.value }))}
                placeholder={t.manageSubject.namePlaceholder}
                dir="rtl"
                style={{ fontFamily: "'Amiri', serif", fontSize: "1.05em" }}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>{t.common.cancel}</Button>
          <Button variant="primary" onClick={handleSave}>{t.common.save}</Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
